import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { SearchClubsPanel } from './SearchClubsPanel';
import {
  X,
  Building2,
  CheckCircle,
  Clock,
  Search,
  MapPin,
  UserCheck,
  AlertCircle,
  Layers,
} from 'lucide-react';
import { useCoachAffiliations } from '@/hooks/useCoachAffiliations';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { ClubAvatar } from '@/layouts/CoachLayout';

// ── Design tokens (copied verbatim from StudentDetailPanel) ───────────────────
const S = {
  panel: {
    background: '#FFFFFF',
    border: '1px solid rgba(15,23,42,0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, -20px 0 60px rgba(15,23,42,0.12), 0 32px 64px rgba(15,23,42,0.10)',
  } as React.CSSProperties,
  actionTile: {
    background: '#FFFFFF',
    border: '1px solid rgba(15,23,42,0.08)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)',
  } as React.CSSProperties,
  iconBubble: (color: string) => ({
    background: `${color}18`,
    border: `1px solid ${color}30`,
  } as React.CSSProperties),
  primaryCTA: {
    background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.20) inset, 0 4px 14px rgba(42,68,148,0.28)',
  } as React.CSSProperties,
};


// ── Props ─────────────────────────────────────────────────────────────────────
interface AffiliationsPanelProps {
  open: boolean;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
export const AffiliationsPanel: React.FC<AffiliationsPanelProps> = ({ open, onClose }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const { t } = useAppTranslations();
  const {
    affiliations,
    activeAffiliations,
    pendingAffiliations,
    isLoading,
    acceptAffiliation,
    leaveClub,
    isAccepting,
    isLeaving,
  } = useCoachAffiliations();

  const panel = open ? createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-30" onClick={onClose} />

      {/* Panel */}
      <div
        className="fixed right-0 sm:right-6 top-0 sm:top-6 bottom-0 sm:bottom-6 w-full sm:w-[420px] bg-white rounded-none sm:rounded-2xl flex flex-col z-[55] overflow-hidden"
        style={S.panel}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="px-6 border-b flex-shrink-0" style={{ borderColor: 'rgba(15,23,42,0.07)', paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))', paddingBottom: '1.25rem' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'rgba(15,23,42,0.35)' }}>
                {t.coach.affiliations.eyebrow}
              </p>
              <p className="text-[17px] font-bold mt-1 truncate" style={{ color: '#0F172A' }}>
                {t.coach.affiliations.title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 flex items-center justify-center transition-colors"
              style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)' }}
            >
              <X className="w-4 h-4" style={{ color: '#64748B' }} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto" style={{ background: '#F8FAFC' }}>

          {isLoading ? (
            <div className="px-5 py-5 space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-[72px] rounded-2xl animate-pulse" style={{ background: 'rgba(15,23,42,0.06)' }} />
              ))}
            </div>
          ) : affiliations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: 'rgba(15,23,42,0.05)', border: '1px solid rgba(15,23,42,0.08)' }}
              >
                <Building2 className="w-7 h-7" style={{ color: 'rgba(15,23,42,0.25)' }} />
              </div>
              <p className="text-[14px] font-bold mb-1" style={{ color: '#0F172A' }}>{t.coach.affiliations.noAffiliationsTitle}</p>
              <p className="text-[12px] leading-relaxed" style={{ color: '#94A3B8' }}>
                {t.coach.affiliations.noAffiliationsBody}
              </p>
            </div>
          ) : (
            <div className="px-5 py-5 space-y-5">

              {/* Stats row — filter-header style: label top-left, icon top-right, big number bottom */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: t.coach.affiliations.statsActive,  value: activeAffiliations.length,  icon: CheckCircle, iconColor: '#16A34A', iconBg: 'rgba(22,163,74,0.12)',  bg: '#FFFFFF', border: 'rgba(15,23,42,0.08)', numColor: '#0F172A' },
                  { label: t.coach.affiliations.statsPending, value: pendingAffiliations.length, icon: Clock,       iconColor: '#B45309', iconBg: 'rgba(180,83,9,0.12)',  bg: '#FFFFFF', border: 'rgba(15,23,42,0.08)', numColor: '#0F172A' },
                  { label: t.coach.affiliations.statsTotal,   value: affiliations.length,        icon: Layers,      iconColor: '#2A4494', iconBg: 'rgba(42,68,148,0.12)', bg: '#FFFFFF', border: 'rgba(15,23,42,0.08)', numColor: '#0F172A' },
                ].map(({ label, value, icon: Icon, iconColor, iconBg, bg, border, numColor }) => (
                  <div
                    key={label}
                    className="px-3 pt-3 pb-3 rounded-xl flex flex-col gap-2"
                    style={{
                      background: bg,
                      border: `1px solid ${border}`,
                      boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)',
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-widest leading-tight" style={{ color: 'rgba(15,23,42,0.40)' }}>{label}</span>
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
                        <Icon className="w-3 h-3" style={{ color: iconColor }} />
                      </div>
                    </div>
                    <span className="text-[26px] font-bold leading-none" style={{ color: numColor }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Pending invites */}
              {pendingAffiliations.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(15,23,42,0.35)' }}>
                    {t.coach.affiliations.sectionPending}
                  </p>
                  <div className="space-y-2">
                    {pendingAffiliations.map(aff => (
                      <AffiliationTile
                        key={aff.id}
                        affiliation={aff}
                        onAccept={() => acceptAffiliation(aff.id)}
                        onLeave={() => leaveClub(aff.id)}
                        isAccepting={isAccepting}
                        isLeaving={isLeaving}
                        labels={{ clubInvitedYou: t.coach.affiliations.clubInvitedYou, awaitingResponse: t.coach.affiliations.awaitingResponse, accept: t.coach.affiliations.accept, decline: t.coach.affiliations.decline, leaveClub: t.coach.affiliations.leaveClub, statusActive: t.coachAffiliations.statusActive, statusPending: t.coachAffiliations.statusPending, statusInvite: t.coachAffiliations.statusInvite }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Active clubs */}
              {activeAffiliations.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(15,23,42,0.35)' }}>
                    {t.coach.affiliations.sectionActive}
                  </p>
                  <div className="space-y-2">
                    {activeAffiliations.map(aff => (
                      <AffiliationTile
                        key={aff.id}
                        affiliation={aff}
                        onAccept={() => acceptAffiliation(aff.id)}
                        onLeave={() => leaveClub(aff.id)}
                        isAccepting={isAccepting}
                        isLeaving={isLeaving}
                        labels={{ clubInvitedYou: t.coach.affiliations.clubInvitedYou, awaitingResponse: t.coach.affiliations.awaitingResponse, accept: t.coach.affiliations.accept, decline: t.coach.affiliations.decline, leaveClub: t.coach.affiliations.leaveClub, statusActive: t.coachAffiliations.statusActive, statusPending: t.coachAffiliations.statusPending, statusInvite: t.coachAffiliations.statusInvite }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          className="px-6 py-4 flex-shrink-0 grid grid-cols-2 gap-2"
          style={{ borderTop: '1px solid rgba(15,23,42,0.07)' }}
        >
          <button
            onClick={onClose}
            className="w-full h-10 rounded-xl text-[13px] font-semibold transition-colors"
            style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)', color: '#64748B', borderRadius: 12 }}
          >
            {t.coach.affiliations.close}
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            className="w-full h-10 rounded-xl text-[13px] font-bold text-white flex items-center justify-center gap-1.5 transition-opacity"
            style={{ ...S.primaryCTA, borderRadius: 12 }}
          >
            <Search className="w-3.5 h-3.5" />
            {t.coach.affiliations.searchClubs}
          </button>
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <>
      {panel}
      <SearchClubsPanel open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

// ── Affiliation tile ──────────────────────────────────────────────────────────

type AffiliationType = ReturnType<typeof useCoachAffiliations>['affiliations'][number];

interface AffiliationTileProps {
  affiliation: AffiliationType;
  onAccept: () => void;
  onLeave: () => void;
  isAccepting: boolean;
  isLeaving: boolean;
  labels: {
    clubInvitedYou: string;
    awaitingResponse: string;
    accept: string;
    decline: string;
    leaveClub: string;
    statusActive: string;
    statusPending: string;
    statusInvite: string;
  };
}

const AffiliationTile: React.FC<AffiliationTileProps> = ({
  affiliation,
  onAccept,
  onLeave,
  isAccepting,
  isLeaving,
  labels,
}) => {
  const isActive  = affiliation.status === 'active';
  const isPending = affiliation.status === 'pending';
  const isInvite  = isPending && affiliation.invitedByClub;

  const clubName = affiliation.club?.name ?? 'Unknown Club';

  return (
    <div
      className="rounded-2xl px-4 py-3 flex items-start gap-3"
      style={{
        background: '#FFFFFF',
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)',
      }}
    >
      <ClubAvatar
        name={clubName}
        logoUrl={affiliation.club?.logo_url ?? null}
        size={36}
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Name + status pill */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[13px] font-semibold truncate" style={{ color: '#0F172A' }}>{clubName}</span>
          <StatusPill status={affiliation.status} invitedByClub={affiliation.invitedByClub} activeLabel={labels.statusActive} pendingLabel={labels.statusPending} inviteLabel={labels.statusInvite} />
        </div>

        {/* Meta line */}
        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          {affiliation.club?.location && (
            <span className="flex items-center gap-1 text-[11px]" style={{ color: '#94A3B8' }}>
              <MapPin className="w-2.5 h-2.5" />
              {affiliation.club.location}
            </span>
          )}
          {affiliation.acceptedAt && (
            <span className="flex items-center gap-1 text-[11px]" style={{ color: '#94A3B8' }}>
              <UserCheck className="w-2.5 h-2.5" style={{ color: '#52B788' }} />
              {new Date(affiliation.acceptedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
          {isInvite && (
            <span className="flex items-center gap-1 text-[11px]" style={{ color: '#B45309' }}>
              <AlertCircle className="w-2.5 h-2.5" />
              {labels.clubInvitedYou}
            </span>
          )}
          {isPending && !isInvite && (
            <span className="text-[11px]" style={{ color: '#94A3B8' }}>{labels.awaitingResponse}</span>
          )}
        </div>

        {/* Accept / Decline for invites */}
        {isInvite && (
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={onAccept}
              disabled={isAccepting}
              className="h-7 px-3 text-[11px] font-bold text-white disabled:opacity-50 flex items-center gap-1"
              style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)', borderRadius: 8 }}
            >
              <CheckCircle className="w-3 h-3" />
              {labels.accept}
            </button>
            <button
              onClick={onLeave}
              disabled={isLeaving}
              className="h-7 px-3 text-[11px] font-semibold disabled:opacity-50 transition-colors"
              style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.10)', color: '#64748B', borderRadius: 8 }}
            >
              {labels.decline}
            </button>
          </div>
        )}

        {/* Leave for active */}
        {isActive && (
          <button
            onClick={onLeave}
            disabled={isLeaving}
            className="mt-2.5 h-7 px-3 text-[11px] font-semibold disabled:opacity-50 transition-colors"
            style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.18)', color: '#DC2626', borderRadius: 8 }}
          >
            {labels.leaveClub}
          </button>
        )}
      </div>

    </div>
  );
};

// ── StatusPill ────────────────────────────────────────────────────────────────
const StatusPill: React.FC<{ status: string; invitedByClub?: boolean; activeLabel: string; pendingLabel: string; inviteLabel: string }> = ({ status, invitedByClub, activeLabel, pendingLabel, inviteLabel }) => {
  if (status === 'active') {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold flex-shrink-0"
        style={{ background: '#D1FAE5', color: '#047857', borderRadius: 6 }}
      >
        <CheckCircle className="w-2.5 h-2.5" />{activeLabel}
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold flex-shrink-0"
        style={{ background: '#FEF3C7', color: '#B45309', borderRadius: 6 }}
      >
        <Clock className="w-2.5 h-2.5" />{invitedByClub ? inviteLabel : pendingLabel}
      </span>
    );
  }
  return null;
};
